import { GetStaticPaths, GetStaticProps } from 'next';

import NextLink from 'next/link';

import { Badge, Box, Container, Divider, HStack, Heading, Link, Stack, Text, VStack } from '@chakra-ui/react';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Document } from '@contentful/rich-text-types';

import {
  TypeArea,
  TypeAreaSkeleton,
  TypeCity,
  TypeCitySkeleton,
  TypeJobAdvert,
  TypeJobAdvertSkeleton
} from 'generated/contentful';

import { ParsedUrlQuery } from 'querystring';

import { RECRUIT_PATH_PREFIX } from 'common/constants';

import { createClient } from 'common/lib/contentful';
import { getFirstOfMonth, getLastOfMonth } from 'common/lib/date';
import { shuffle } from 'common/lib/utils';

import RichText from 'common/components/Contentful/RichText';
import Layout from 'common/components/Layout/Layout';
import LinkButton from 'common/components/LinkButton/LinkButton';

import JobAdvertSchema from 'features/jobs-board/components/JobAdvert.schema';
import SimilarJobList from 'features/jobs-board/components/SimilarJobList';

import { fonts } from 'theme/fonts';

interface RecruitJobAdvertParams extends ParsedUrlQuery {
  city: string;
  area: string;
  jobAdvert: string;
}

interface RecruitJobAdvertProps {
  area: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string> | null;
  city: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  otherAreas: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[];
  jobAdvert: TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  publishedDate: string;
  closingDate: string;
}

export const getStaticPaths: GetStaticPaths<RecruitJobAdvertParams> = async () => {
  const client = createClient();

  const cities = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city'
  });

  const areas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    limit: 1000,
    include: 3
  });

  const jobAdverts = await client.withoutUnresolvableLinks.getEntries<TypeJobAdvertSkeleton>({
    content_type: 'jobAdvert'
  });

  const areaPaths = areas.items
    .flatMap(area => jobAdverts.items.map(jobAdvert => ({ jobAdvert, area })))
    .map(({ jobAdvert, area }) => ({
      params: {
        city: area.fields.city!.fields.slug,
        area: area.fields.slug,
        jobAdvert: jobAdvert.fields.slug
      }
    }));

  // Paths for city job adverts.
  // We use the city slug for both `city` and `area` route params for these pages.
  const cityPaths = cities.items
    .flatMap(city => jobAdverts.items.map(jobAdvert => ({ jobAdvert, city })))
    .map(({ jobAdvert, city }) => ({
      params: {
        city: city.fields.slug,
        area: city.fields.slug,
        jobAdvert: jobAdvert.fields.slug
      }
    }));

  return {
    paths: [...cityPaths, ...areaPaths],
    fallback: false
  };
};

export const getStaticProps: GetStaticProps<RecruitJobAdvertProps> = async context => {
  const client = createClient();

  const { city: citySlug, area: areaSlug, jobAdvert: jobAdvertSlug } = context.params as RecruitJobAdvertParams;

  const area = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    'fields.slug[in]': [areaSlug]
  });

  const city = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    'fields.slug[in]': [citySlug]
  });

  const otherAreas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    'fields.slug[ne]': areaSlug,
    'fields.city.sys.contentType.sys.id': 'city',
    'fields.city.fields.slug[in]': [citySlug]
  });

  const jobAdvert = await client.withoutUnresolvableLinks.getEntries<TypeJobAdvertSkeleton>({
    content_type: 'jobAdvert',
    'fields.slug[in]': [jobAdvertSlug]
  });

  jobAdvert.items[0].fields.employmentType.sort();

  const publishedDate = getFirstOfMonth();
  // Closing date of an application is end of current month (used in schema)
  const closingDate = getLastOfMonth();

  // otherAreas array is unbiasedly shuffled and first 4 areas are picked
  return {
    props: {
      area: area.items.length ? (area.items[0] as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>) : null,
      city: city.items[0] as TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      otherAreas: shuffle<TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>>(
        otherAreas.items as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
      ).slice(0, 4),
      jobAdvert: jobAdvert.items[0] as TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      publishedDate,
      closingDate
    }
  };
};

export default function RecruitJobAdvert({
  area,
  city,
  otherAreas,
  jobAdvert,
  publishedDate,
  closingDate
}: RecruitJobAdvertProps) {
  const emailSubject = `Housekeep Jobs - ${jobAdvert.fields.employmentType.join(' or ')} ${
    jobAdvert.fields.title
  } required in ${(area || city).fields.name}`;
  const emailBody = `Hello,%0AI found this job on Housekeep Jobs, which I think you might find interesting.%0APlease check it out: https://housekeep.com${RECRUIT_PATH_PREFIX}/jobs/${
    city.fields.slug
  }/${(area || city).fields.slug}/${jobAdvert.fields.slug}/${publishedDate}/`;

  const displayRate =
    jobAdvert.fields.minimumPay === jobAdvert.fields.maximumPay
      ? `£${jobAdvert.fields.minimumPay.toFixed(2)}`
      : `£${jobAdvert.fields.minimumPay.toFixed(2)} - £${jobAdvert.fields.maximumPay.toFixed(2)}`;

  // Strip HTML Tags from the Description Rich Text Field for Layout and Schema
  const plainStringDescription = jobAdvert.fields.description
    ? documentToPlainTextString(jobAdvert.fields.description)
    : '';

  return (
    <Layout
      pageTitle={`${jobAdvert.fields.title} Job - ${jobAdvert.fields.employmentType.join(' or ')} - ${
        (area || city).fields.name
      }`}
      description={plainStringDescription}
      showStatusBar={false}
      nav="recruit"
    >
      <JobAdvertSchema
        jobAdvert={jobAdvert}
        area={area}
        city={city}
        publishedDate={publishedDate}
        closingDate={closingDate}
      />
      <Container maxW="container.lg" py={[12, null, 16]}>
        <Stack direction={['column', null, 'row']} spacing={[10, null, 20]} alignItems="flex-start">
          <Box w={['100%', null, '66%']}>
            <Badge mb={2} colorScheme="green" fontSize={['md', null, 'lg']} variant="subtle" borderRadius="md">
              Now Hiring
            </Badge>
            <Heading size="2xl" mb={2}>
              {jobAdvert.fields.title} - {jobAdvert.fields.employmentType.join(' or ')}
            </Heading>
            <Heading size="md" mb={2} fontFamily={fonts.body} fontWeight="medium">
              {(area || city).fields.name}
            </Heading>
            <Heading size="md" mb={4} fontFamily={fonts.body} fontWeight="medium">
              {displayRate} Per {jobAdvert.fields.payFrequency}
            </Heading>
            <HStack spacing={4} mb={6}>
              <LinkButton href={`${RECRUIT_PATH_PREFIX}/apply/`} colorScheme="blue" size="lg">
                Apply Now
              </LinkButton>
              <LinkButton href={`mailto:?subject=${emailSubject}&body=${emailBody}`} colorScheme="gray" size="lg">
                Share Job
              </LinkButton>
            </HStack>

            <Text fontWeight="medium" fontSize="xl" color="gray.500" mb={4}>
              Job Description
            </Text>
            <Box mb={8}>
              <RichText document={jobAdvert.fields.description as Document} />
            </Box>
            <Box
              borderWidth={1}
              borderRadius="md"
              borderColor="gray.100"
              boxShadow="md"
              textAlign="center"
              p={[4, null, 8]}
            >
              <Text fontSize="xl" mb={4}>
                Just hit the{' '}
                <Text as="span" fontWeight="medium">
                  'Apply Now'
                </Text>{' '}
                button below to complete our online registration
              </Text>
              <LinkButton
                href={`${RECRUIT_PATH_PREFIX}/apply/`}
                colorScheme="blue"
                size="xl"
                minW="40%"
                w={['100%', null, 'auto']}
              >
                Apply Now
              </LinkButton>
            </Box>
          </Box>

          <Box w={['100%', null, '34%']}>
            <VStack spacing={5} alignItems="flex-start">
              <Link
                as={NextLink}
                href={`${RECRUIT_PATH_PREFIX}/jobs/${city.fields.slug}/`}
                fontWeight="medium"
                fontSize="xl"
              >
                Search For Jobs
              </Link>

              <Divider />

              <Link as={NextLink} href={`${RECRUIT_PATH_PREFIX}/`} fontWeight="medium" fontSize="xl">
                Working at Housekeep
              </Link>

              <Divider />

              <Text fontWeight="medium" fontSize="xl" color="gray.500" mb={4}>
                Similar Jobs
              </Text>

              <Box width="100%">
                <SimilarJobList job={jobAdvert} areas={otherAreas} />
              </Box>
            </VStack>
          </Box>
        </Stack>
      </Container>
    </Layout>
  );
}
